import { apiFetch } from "@shared/api/api";
import {
  DEFAULT_TENANT_NAME,
  fetchClientIp,
  hydrateSession,
} from "@shared/api/sessionHydrate";
import { global, useMenuStore } from "@shared/libs/Global";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError } from "@shared/type/ApiType";
import { UseApiService } from "@shared/hooks/useApiService";
import { removeFcmToken } from "@shared/libs/firebasePush";

export const UseAuthService = () => {
  // 로그인 직후 1회 라우팅/복귀 처리에 사용하는 세션 키들
  const MENU_ROUTE_ALLOW_KEY = "allow_menu_route_once";
  const JUST_LOGGED_IN_KEY = "just_logged_in";
  const SKIP_MENU_ROUTE_KEY = "skip_menu_route_once";
  const RETURN_PATH_KEY = "return_path";
  const SESSION_FLAG_TRUE = "true";
  const ipAddress = useRef<string>("");
  const cdFirm = useRef<string>("");
  const cdUser = useRef<string>("");
  const tenantId = useRef<string>("");
  const tenantName = useRef<string>("");

  const { apiPost } = UseApiService();

  const navigate = useNavigate();
  const { clearExpandedMap } = useMenuStore();

  const login = async (id: string, password: string, tenant: string) => {
    await fetchIp();

    try {
      const response = await apiFetch("/login", {
        method: "POST",
        body: {
          cd_user: id,
          no_pwd: password,
        } as any,
        headers: {
          "X-TENANT-ID": tenant,
        },
      });

      if (response) {
        // BE 응답에 tenantId가 없으면 로그인에 사용한 tenant를 그대로 유지한다.
        // 여기서 DEFAULT로 굳으면 로그아웃 시 /login?tenant=DEFAULT 로 나가서
        // 다음 로그인이 테넌트 없이 잡힌다.
        tenantId.current = response.data?.tenantId || tenant || "DEFAULT";
        if (response.data?.tenantId) {
          tenantName.current = response.data?.tenantName || "지투시스넷";
        }
        await afterLoginProcess(tenant);
      }

      if (!ipAddress || !cdFirm.current || !cdUser.current) {
        throw ApiError.badRequest("로그인에 실패하였습니다.");
      }

      // SSO/공유 링크 진입 시 저장된 return_path가 있으면 해당 경로로 복귀
      const returnPath = sessionStorage.getItem(RETURN_PATH_KEY);
      if (returnPath) {
        sessionStorage.removeItem(RETURN_PATH_KEY);
        sessionStorage.setItem(MENU_ROUTE_ALLOW_KEY, SESSION_FLAG_TRUE);
        sessionStorage.removeItem(JUST_LOGGED_IN_KEY);
        sessionStorage.removeItem(SKIP_MENU_ROUTE_KEY);
        navigate(returnPath, { replace: true });
      } else {
        sessionStorage.setItem(JUST_LOGGED_IN_KEY, SESSION_FLAG_TRUE);
        navigate("/MainLayout", { replace: true });
      }
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      } else {
        throw ApiError.badRequest("로그인에 실패하였습니다.");
      }
    }
  };

  // 사용자/시스템 설정 로딩은 SSO 진입(ShareLanding)과 공유한다.
  const afterLoginProcess = async (tenant: string) => {
    const userConfig = await hydrateSession({
      tenantId: tenant,
      storeTenantId: tenantId.current || tenant || "DEFAULT",
      tenantName: tenantName.current || DEFAULT_TENANT_NAME,
      dcIp: ipAddress.current,
    });

    cdFirm.current = userConfig.cd_firm;
    cdUser.current = userConfig.cd_user;
  };

  const logout = () => {
    if (window.confirm("로그아웃하시겠습니까?")) {
      // 로그아웃 시 라우팅 관련 플래그 정리
      sessionStorage.removeItem(RETURN_PATH_KEY);
      sessionStorage.removeItem(MENU_ROUTE_ALLOW_KEY);
      sessionStorage.removeItem(JUST_LOGGED_IN_KEY);
      sessionStorage.setItem(SKIP_MENU_ROUTE_KEY, SESSION_FLAG_TRUE);
      clearExpandedMap();
      // FCM 토큰 삭제 후 로그아웃 호출 (세션이 살아있는 동안 remove API가 처리되도록 순서 보장)
      removeFcmToken()
        .catch(() => {})
        .finally(() => {
          apiFetch("/logout", {
            method: "POST",
            headers: {
              "X-TENANT-ID": global.getState().userInfo.tenant_id || "DEFAULT",
            },
          }).catch((e) => {});
        });

      // DEFAULT는 파라미터를 붙이지 않는다 — 로그인 화면이 ?tenant=DEFAULT를
      // "등록되지 않은 테넌트"로 오탐해서 replace("/login")으로 파라미터를 떼고,
      // 그 과정에서 전체 리로드가 한 번 더 일어난다.
      const tenantId = global.getState().userInfo.tenant_id || "DEFAULT";
      navigate(tenantId === "DEFAULT" ? "/login" : "/login?tenant=" + tenantId, {
        replace: true,
      });
    }
  };

  const fetchIp = async () => {
    ipAddress.current = await fetchClientIp();
  };

  return {
    login,
    logout,
  };
};
